import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { FunnelCta } from "../../lib/tracking";
import PlusConversionModal from "./PlusConversionModal";

/**
 * Quem hospeda a modal de conversão, e como os banners lhe chamam.
 *
 * A modal NÃO SUBSTITUI banner nenhum: os dez formatos ficam onde estão, com as
 * palavras que têm. O que muda é o destino do clique — em vez de sair para o
 * /checkout/, abre-se isto por cima, e é daqui que se sai para o checkout.
 *
 * UMA INSTÂNCIA PARA TODA A APLICAÇÃO, no topo da árvore. Treze sítios chamam o
 * PlusCtaLink e nenhum deles devia montar um <dialog> próprio: seriam treze
 * cópias do trap de foco, do bloqueio do scroll e do Escape, e dois banners na
 * mesma tela abririam duas modais.
 *
 * O provider é opcional de propósito — ver usePlusCta em plus-cta.tsx. Sem ele
 * o link volta a navegar direto para o checkout, que é o comportamento anterior
 * e o que qualquer render fora da app (um teste, uma tela isolada) deve ter.
 */

interface ConversionModalApi {
  /** Abre a modal a partir do CTA que a pediu. */
  open: (origin: FunnelCta) => void;
}

const ConversionModalContext = createContext<ConversionModalApi | null>(null);

/** Null quando não há provider — o chamador decide o que fazer sem ele. */
export function useConversionModal(): ConversionModalApi | null {
  return useContext(ConversionModalContext);
}

export function ConversionModalProvider({
  children,
  scope = "",
}: {
  children: ReactNode;
  /** A classe de tema a passar à modal; ver PlusModal para o porquê. */
  scope?: string;
}) {
  const [origin, setOrigin] = useState<FunnelCta | null>(null);

  const api = useMemo<ConversionModalApi>(
    () => ({ open: (next: FunnelCta) => setOrigin(next) }),
    []
  );

  const close = useCallback(() => setOrigin(null), []);

  return (
    <ConversionModalContext.Provider value={api}>
      {children}
      {origin ? (
        <PlusConversionModal onClose={close} origin={origin} scope={scope} />
      ) : null}
    </ConversionModalContext.Provider>
  );
}
